import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeftIcon, ArrowRightIcon, ChevronRightIcon, PauseIcon, PlayIcon } from "lucide-react";
import { HERO_SLIDES } from "../../data/gallery";
import { EASE } from "../../utils/motion";
const SLIDE_MS = 6000;
export function HeroSlideshow() {
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);
  const playing = !paused && !reduce;
  const go = useCallback((delta: number) => {
    setDirection(delta);
    setIndex(i => (i + delta + HERO_SLIDES.length) % HERO_SLIDES.length);
  }, []);
  useEffect(() => {
    if (!playing) return;
    const t = window.setTimeout(() => go(1), SLIDE_MS);
    return () => window.clearTimeout(t);
  }, [index, playing, go]);
  const slide = HERO_SLIDES[index];
  return <section aria-roledescription="carousel" aria-label="MCC gallery highlights" tabIndex={0} onKeyDown={e => {
    if (e.key === "ArrowRight") go(1);
    if (e.key === "ArrowLeft") go(-1);
  }} className="relative h-screen min-h-[640px] overflow-hidden bg-[#061f38] text-white focus:outline-none" data-id="element-193" data-mp="f44grulnqxn4">
      <AnimatePresence initial={false} custom={direction} data-id="element-194" data-mp="1e10rlddg064">
        <motion.div key={index} custom={direction} initial={{
        clipPath: reduce ? "inset(0 0 0 0)" : direction > 0 ? "inset(0 0 0 100%)" : "inset(0 100% 0 0)"
      }} animate={{
        clipPath: "inset(0 0% 0 0%)"
      }} exit={{
        opacity: reduce ? 0 : 0.6
      }} transition={{
        duration: reduce ? 0.01 : 1.1,
        ease: EASE
      }} drag="x" dragConstraints={{
        left: 0,
        right: 0
      }} dragElastic={0.12} onDragEnd={(_, info) => {
        if (info.offset.x < -80) go(1);
        if (info.offset.x > 80) go(-1);
      }} className="absolute inset-0 cursor-grab active:cursor-grabbing" data-id="element-195" data-mp="y1e4vp1xgzap">
          <motion.img src={slide.src} alt={slide.title} draggable={false} initial={{
          scale: reduce ? 1 : 1.15
        }} animate={{
          scale: reduce ? 1 : 1.02
        }} transition={{
          duration: reduce ? 0 : SLIDE_MS / 1000 + 1.2,
          ease: "linear"
        }} className="h-full w-full select-none object-cover" data-id="element-196" data-mp="3b9ouvm1hichw" />
          <div className="absolute inset-0 bg-[#061f38]/35" data-id="element-197" data-mp="2nhrm63tc00l0" />
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-[#061f38]/35" data-id="element-198" data-mp="2sbvsckurw4l1" />
        </motion.div>
      </AnimatePresence>

      <div className="pointer-events-none relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-between px-5 pb-10 pt-28 md:px-9" data-id="element-199" data-mp="2djbthwenoj9e">
        <nav aria-label="Breadcrumb" className="pointer-events-auto flex items-center gap-2 text-xs text-white/65" data-id="element-200" data-mp="24kgkkck88v2e">
          <Link to="/" className="transition-colors duration-200 hover:text-white" data-id="element-201" data-mp="29irxiw5tjlou">Home</Link>
          <ChevronRightIcon size={13} data-id="element-202" data-mp="1pz2p8bnc1rnn" />
          <span className="text-white/90" data-id="element-203" data-mp="1q043sq3k4wa4">Gallery</span>
        </nav>

        <div data-id="element-204" data-mp="1l087ze50kb5n">
          <p className="text-xs font-semibold tracking-[0.2em] text-[#f0b1b9]" data-id="element-205" data-mp="1l0rhq7kift8l">THE MCC GALLERY</p>
          <div className="mt-6 h-[2.1em] overflow-hidden text-[clamp(2.6rem,6.5vw,6.5rem)] font-medium leading-[1.02] tracking-[-0.065em] md:h-[1.05em]" data-id="element-206" data-mp="1z00xj64t0lc1">
            <AnimatePresence mode="wait" data-id="element-207" data-mp="28wc8nssnr54w">
              <motion.h1 key={index} initial={{
              y: reduce ? 0 : "100%"
            }} animate={{
              y: "0%"
            }} exit={{
              y: reduce ? 0 : "-100%"
            }} transition={{
              duration: reduce ? 0.01 : 0.7,
              ease: EASE
            }} className="max-w-5xl" data-id="element-208" data-mp="28v18e2yf8fij">
                {slide.title}
              </motion.h1>
            </AnimatePresence>
          </div>

          <div className="pointer-events-auto mt-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-209" data-mp="2rl0qu6zrawcz">
            <div className="flex w-full max-w-xl gap-2" data-id="element-210" data-mp="kzct8n9h283k">
              {HERO_SLIDES.map((s, i) => <button key={s.label} type="button" onClick={() => {
              setDirection(i > index ? 1 : -1);
              setIndex(i);
            }} className="group flex-1 text-left" aria-label={`Show ${s.label}`} aria-current={i === index} data-id="element-211" data-mp="pxo5f4n8ouoy" data-mpi={i}>
                  <span className="block h-[2px] w-full overflow-hidden bg-white/25" data-id="element-212" data-mp="yqrg556i2tfw">
                    {i === index ? <motion.span key={`${index}-${playing}`} initial={{
                  scaleX: playing ? 0 : 1
                }} animate={{
                  scaleX: 1
                }} transition={{
                  duration: playing ? SLIDE_MS / 1000 : 0,
                  ease: "linear"
                }} className="block h-full origin-left bg-white" data-id="element-213" data-mp="21tjfhf6wlg3" /> : <span className={`block h-full bg-white ${i < index ? "w-full opacity-60" : "w-0"}`} data-id="element-214" data-mp="g09zhyyxzoaw" />}
                  </span>
                  <span className={`mt-3 hidden text-[11px] tracking-[0.12em] transition-colors duration-200 sm:block ${i === index ? "text-white" : "text-white/45 group-hover:text-white/80"}`} data-id="element-215" data-mp="1migd4yu9o636">{s.label.toUpperCase()}</span>
                </button>)}
            </div>
            <div className="flex items-center gap-2" data-id="element-216" data-mp="1wksphbdreq78">
              <span className="mr-3 text-sm tabular-nums text-white/70" data-id="element-217" data-mp="1rssmtb6b1j6y">0{index + 1} / 0{HERO_SLIDES.length}</span>
              <button type="button" onClick={() => go(-1)} className="grid h-11 w-11 place-items-center border border-white/40 transition-colors duration-200 hover:border-white hover:bg-white hover:text-[#061f38]" aria-label="Previous slide" data-id="element-218" data-mp="1nqnuqgvi8umf"><ArrowLeftIcon size={17} data-id="element-219" data-mp="1ds7zc1pg2z74" /></button>
              <button type="button" onClick={() => setPaused(p => !p)} className="grid h-11 w-11 place-items-center border border-white/40 transition-colors duration-200 hover:border-white hover:bg-white hover:text-[#061f38]" aria-label={paused ? "Play slideshow" : "Pause slideshow"} data-id="element-220" data-mp="18tws7yqjb2sl">{paused ? <PlayIcon size={16} data-id="element-221" data-mp="2z6vxaiaxs5y3" /> : <PauseIcon size={16} data-id="element-222" data-mp="2z6m6wtlzbwrv" />}</button>
              <button type="button" onClick={() => go(1)} className="grid h-11 w-11 place-items-center bg-[#c5233d] transition-colors duration-200 hover:bg-[#a71931]" aria-label="Next slide" data-id="element-223" data-mp="2z4rl7d2vw0mq"><ArrowRightIcon size={17} data-id="element-224" data-mp="11bp4t50l1b01" /></button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}